import { NotifyHelper } from 'classes/helpers/notify.helper';
import { t } from 'i18next';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { SessionEventsRoutes } from 'lib_ts/enums/routing/routes/session-events';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import {
  IFireEvent,
  ILoginEvent,
  IMSTargetEvent,
  ISessionEvent,
  ISessionEventDetails,
} from 'lib_ts/interfaces/i-session-event';
import {
  IMachineSessionSummaryRequest,
  ISessionSummary,
  IUserSessionSummaryRequest,
} from 'lib_ts/interfaces/i-session-summary';
import { ICommonPitchRecap } from 'lib_ts/interfaces/pitches/i-common-pitch-recap';
import { BaseRESTService } from 'services/_base-rest.service';

export const getShortenedSession = (id: string | undefined): string => {
  if (!id) {
    return 'empty-id';
  }

  const parts = id?.split('-');
  const index = id.substring(id.lastIndexOf('.'));
  const shortSession =
    parts.length === 6
      ? `${parts[0]}-${parts[1]}*${index}`
      : `${parts[0]}*${index}`;
  return shortSession;
};

export class SessionEventsService extends BaseRESTService {
  private static instance: SessionEventsService;

  private static getInstance(): SessionEventsService {
    if (!SessionEventsService.instance) {
      SessionEventsService.instance = new SessionEventsService();
    }

    return SessionEventsService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Sessions,
    });
  }

  /** should only return sessions which contain at least 1 fire event */
  static async summarizeUserSessions(
    payload: IUserSessionSummaryRequest
  ): Promise<ISessionSummary[]> {
    return await this.getInstance().post(
      {
        uri: SessionEventsRoutes.SummarizeUser,
      },
      payload
    );
  }

  /** should only return sessions which contain at least 1 fire event */
  static async summarizeMachinesSessions(
    payload: IMachineSessionSummaryRequest
  ): Promise<ISessionSummary[]> {
    return await this.getInstance().post(
      {
        uri: SessionEventsRoutes.SummarizeMachines,
      },
      payload
    );
  }

  static async createOne(
    data: Partial<ISessionEventDetails>
  ): Promise<IServerResponse> {
    return await this.getInstance().post(
      {
        uri: SessionEventsRoutes.CreateOne,
      },
      {
        ...data,
        session: sessionStorage.getItem('sessionID'),
      }
    );
  }

  static async getManyFromSession(config: {
    machineID: string;
    env: string;
    session: string;
  }): Promise<ISessionEventDetails[]> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.GetManyFromSession,
      params: {
        machineID: config.machineID,
        env: config.env,
        session: config.session,
      } as any,
    });
  }

  /** returns the (single) login event for the session */
  static async getLogin(session: string): Promise<ILoginEvent> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.GetLogin,
      params: {
        session: session,
      } as any,
    });
  }

  static async updateOne(data: Partial<ISessionEvent>): Promise<ISessionEvent> {
    if (!data._id) {
      throw new Error('Cannot put without id');
    }

    return await this.getInstance().put(
      {
        uri: SessionEventsRoutes.UpdateOne,
        params: {
          event_id: data._id,
        } as any,
      },
      data
    );
  }

  static async exportShots(config: {
    machineID: string;
    session: string;
  }): Promise<IMSTargetEvent[] | undefined> {
    return await this.getInstance().get({
      uri: SessionEventsRoutes.ExportShots,
      params: {
        machineID: config.machineID,
        session: config.session,
      } as any,
    });
  }

  static async getFireByID(fireID: string): Promise<IFireEvent | undefined> {
    return await this.getInstance()
      .get({
        uri: `${SessionEventsRoutes.GetFireByID}/${fireID}`,
      })
      .then((result) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as IFireEvent;
      })
      .catch((e) => {
        console.error(e);
        return undefined;
      });
  }

  static async getRecentRecaps(): Promise<ICommonPitchRecap[] | undefined> {
    return await this.getInstance()
      .get({ uri: SessionEventsRoutes.RecentRecaps })
      .then((result) => {
        if (!result.success) {
          NotifyHelper.warning({
            message_md: result.error ?? t('common.request-failed-msg'),
          });
          return undefined;
        }

        return result.data as ICommonPitchRecap[];
      })
      .catch((e) => {
        console.error(e);
        return undefined;
      });
  }
}
